import { Classes, Icon, Intent, Tree, Menu, MenuItem, Button } from "@blueprintjs/core";
import { ContextMenu2 } from "@blueprintjs/popover2";
import { v4 as uuid } from "uuid";
import { Instruction } from "src/models/Instruction";
import { Dictionary, swapInstructions, TemplateProject, updateInstructionArray } from "src/utils/utils";
import { useCallback, useContext, useMemo } from "react";
import {
    InputInstructionHasDependency,
    NewTemplateInstructionsContext,
    SourceDialogContext,
    SourceDialogCurrentProjectContext
} from "src/utils/context";
import { ListState } from "src/models/TemplateCreationList";

type Props = {
    availableInstructions: Array<TemplateProject>;
    setAvailableInstructions: React.Dispatch<React.SetStateAction<TemplateProject[]>>;
    masterList: Dictionary<ListState>;
    setMasterList: React.Dispatch<React.SetStateAction<Dictionary<ListState>>>;
};

export default function NewTemplateInstructions({
    availableInstructions,
    setAvailableInstructions,
    masterList,
    setMasterList
}: Props) {
    const [sourceDialogOpen, setSourceDialogOpen] = useContext(SourceDialogContext);
    const [, setSourceDialogCurrentProject] = useContext(SourceDialogCurrentProjectContext);
    const [instructions, setInstructions] = useContext(NewTemplateInstructionsContext);
    const [, setInstructionHasDependecy] = useContext(InputInstructionHasDependency);

    const handleUpClick = useCallback(
        (index: number) => {
            const copy = [...instructions];
            swapInstructions(copy, index - 1);
            setInstructions(copy);
        },
        [instructions, setInstructions]
    );

    const handleDownClick = useCallback(
        (index: number) => {
            const copy = [...instructions];
            swapInstructions(copy, index);
            setInstructions(copy);
        },
        [instructions, setInstructions]
    );

    const handleRemoveClick = useCallback(
        (i: Instruction) => {
            if (instructions) {
                // set removed instruction visible in available instructions
                const avCopy = [...availableInstructions!];
                // check againts instructions project id because projects don't have instructions id
                const avIdx = availableInstructions!.findIndex(ai => ai.projectId === i.projectId);
                avCopy[avIdx].visible = true;
                setAvailableInstructions(avCopy);

                // remove set instruction
                const copy = [...instructions];
                const idx = copy.indexOf(i);
                copy.splice(idx, 1);
                // display order needs to be updated to correctly reflect the instructions position
                // in the tree, otherwise some dependencies will not show because of the filter
                // on Add Dependency click
                copy.forEach((inst, index) => (inst.display.order = (index + 1) * 10));
                setInstructions(copy);

                if (masterList[i.projectId]) {
                    const { [i.projectId]: remove, ...newList } = masterList;
                    setMasterList(newList);
                }
            }
        },
        [availableInstructions, instructions, masterList, setAvailableInstructions, setInstructions, setMasterList]
    );

    //!! todo updated this to work with multiple approval steps
    const handlAddApprovalClick = useCallback(
        (i: Instruction) => {
            const copy = [...instructions];
            const index = copy.findIndex(c => c.instructionId === i.instructionId);
            copy[index] = updateInstructionArray(i, "precondition", ["approval"]);
            setInstructions(copy);
        },
        [instructions, setInstructions]
    );

    const handlRemoveApprovalClick = useCallback(
        (i: Instruction) => {
            const copy = [...instructions];
            const index = copy.findIndex(c => c.instructionId === i.instructionId);
            //!! todo: update this when more approval steps are required
            copy[index] = updateInstructionArray(i, "precondition", []);
            setInstructions(copy);
        },
        [instructions, setInstructions]
    );

    const handleAddDependency = useCallback(
        (i: Instruction, dependecyId: string) => {
            // find the index of the instruction we want to update the dependencies of
            const copy = [...instructions];
            const index = copy.findIndex(c => c.instructionId === i.instructionId);
            const dependencies = i.dependsOn as string[];
            if (!dependencies.includes(dependecyId)) {
                copy[index] = updateInstructionArray(i, "dependsOn", [...dependencies, dependecyId]);
                setInstructions(copy);
            }
        },
        [instructions, setInstructions]
    );

    const handleRemoveDependency = useCallback(
        (i: Instruction, dependecyId: string) => {
            const copy = [...instructions];
            const index = copy.findIndex(c => c.instructionId === i.instructionId);
            const dependencies = [...copy[index].dependsOn];
            dependencies.splice(
                dependencies.findIndex(d => d === dependecyId),
                1
            );
            copy[index].dependsOn = dependencies;
            setInstructions(copy);

            const projectId = copy[index].projectId;
            const lists = { ...masterList[projectId] };
            const cleared = lists.dependency
                .filter(d => !d.source || d.source === dependecyId)
                .map(d => {
                    d.mapsTo = undefined;
                    d.source = undefined;
                    return d;
                });

            lists.manual = lists.manual.concat(cleared);
            cleared.forEach(c => {
                const index = lists.dependency.findIndex(d => d.original === c.original);
                lists.dependency.splice(index, 1);
            });
            setMasterList(ml => ({ ...ml, [projectId]: lists }));
        },
        [instructions, masterList, setInstructions, setMasterList]
    );

    const contents = useMemo(() => {
        let totalInstructions = 0;
        return instructions.map((instruction, instructionIndex) => {
            const preconditions = instruction.precondition.map(p => ({
                id: uuid(),
                icon: <Icon icon="confirm" intent={Intent.SUCCESS} className={Classes.TREE_NODE_ICON} />,
                label: <ContextMenu2 content={<></>}>{p}</ContextMenu2>,
                secondaryLabel: (
                    <Button
                        minimal
                        icon="cross"
                        intent={Intent.DANGER}
                        onClick={() => {
                            handlRemoveApprovalClick(instruction);
                        }}
                    />
                )
            }));
            const dependencies = instruction.dependsOn.map(d => ({
                id: uuid(),
                icon: <Icon icon="th-derived" intent={Intent.PRIMARY} className={Classes.TREE_NODE_ICON} />,
                label: (
                    <ContextMenu2 content={<></>}>{instructions.find(a => a.instructionId === d)?.alias}</ContextMenu2>
                ),
                secondaryLabel: (
                    <Button
                        minimal
                        icon="cross"
                        intent={Intent.DANGER}
                        onClick={() => {
                            handleRemoveDependency(instruction, d as any);
                        }}
                    />
                )
            }));
            const children = preconditions.concat(dependencies);

            const node = {
                id: instruction.instructionId,
                icon: "code" as any,
                label: (
                    <ContextMenu2
                        content={
                            <Menu key={instruction.instructionId}>
                                <MenuItem
                                    key={`preconditions_${instruction.instructionId}`}
                                    icon="confirm"
                                    text="Add Precondition"
                                >
                                    <MenuItem
                                        key={`approval_${instruction.instructionId}`}
                                        text="Add Approval"
                                        onClick={() => handlAddApprovalClick(instruction)}
                                    />
                                </MenuItem>
                                <MenuItem
                                    key={`dependency_${instruction.instructionId}`}
                                    icon="th-derived"
                                    text="Add Dependency"
                                    disabled={instructionIndex === 0}
                                >
                                    {instructions
                                        .filter(inst => inst.display.order < instruction.display.order)
                                        .map(inst => (
                                            <MenuItem
                                                key={`dependency_${inst.instructionId}`}
                                                text={inst.alias}
                                                onClick={() => {
                                                    handleAddDependency(instruction, inst.instructionId);
                                                }}
                                            />
                                        ))}
                                </MenuItem>
                                <MenuItem
                                    key={`setsource_${instruction.instructionId}`}
                                    icon="arrow-right"
                                    text="Set Input Source"
                                    onClick={() => {
                                        setSourceDialogOpen(!sourceDialogOpen);
                                        setSourceDialogCurrentProject(
                                            availableInstructions?.find(a => a.projectId === instruction.projectId)
                                        );
                                        // when Set Input Source is clicked check if the instruction has a dependency
                                        // if there is no dependency set the context to false.
                                        // this context is used in the InputSourceDialog.tsx to show or hide the
                                        // dependency result option.
                                        setInstructionHasDependecy(dependencies.length > 0);
                                    }}
                                />
                                <MenuItem
                                    key={`delete_${instruction.instructionId}`}
                                    icon="cross"
                                    text="Remove..."
                                    intent={Intent.DANGER}
                                    onClick={() => handleRemoveClick(instruction)}
                                />
                            </Menu>
                        }
                    >
                        {instruction.alias}
                    </ContextMenu2>
                ),
                secondaryLabel: (
                    <RowIcons
                        index={totalInstructions}
                        isLast={totalInstructions === instructions.length - 1}
                        handleUpClick={handleUpClick}
                        handleDownClick={handleDownClick}
                    />
                ),
                childNodes: children.length > 0 ? children : undefined,
                isExpanded: true,
                hasCaret: false
            };
            totalInstructions++;
            return node;
        });
    }, [
        instructions,
        handleUpClick,
        handleDownClick,
        handlRemoveApprovalClick,
        handleRemoveDependency,
        handlAddApprovalClick,
        handleAddDependency,
        setSourceDialogOpen,
        sourceDialogOpen,
        setSourceDialogCurrentProject,
        availableInstructions,
        setInstructionHasDependecy,
        handleRemoveClick
    ]);

    return <Tree contents={contents} className={Classes.ELEVATION_0} />;
}

type IconProps = {
    index: number;
    isLast: boolean;
    handleUpClick: (index: number) => void;
    handleDownClick: (index: number) => void;
};
function RowIcons({ index, isLast, handleUpClick, handleDownClick }: IconProps) {
    return (
        <div className="tree-icons">
            <Button
                minimal
                icon="arrow-up"
                intent={Intent.PRIMARY}
                disabled={index <= 0}
                onClick={() => handleUpClick(index)}
            />
            <Button
                minimal
                icon="arrow-down"
                intent={Intent.PRIMARY}
                disabled={isLast}
                onClick={() => handleDownClick(index)}
            />
        </div>
    );
}
